import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, Card, CardContent, LinearProgress, useTheme } from '@mui/material';

interface PowerProfileChartProps {
  chartData?: any;
  title?: string;
  height?: number;
}

const PowerProfileChart: React.FC<PowerProfileChartProps> = ({
  chartData,
  title = 'Power Profile Analysis',
  height = 400
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [progressiveIndex, setProgressiveIndex] = React.useState<number>(0);
  const [isAnimating, setIsAnimating] = React.useState<boolean>(false);
  const animationRef = useRef<number | null>(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Dynamic colors based on theme - use useMemo to avoid recreating on every render
  const colors = useMemo(() => ({
    background: isDark ? '#0d1117' : '#ffffff',
    text: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
    grid: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    primary: isDark ? '#4a9eff' : '#1f77b4',
    primaryLight: isDark ? 'rgba(74, 158, 255, 0.3)' : 'rgba(31, 119, 180, 0.1)'
  }), [isDark]);

  // Debug: Log received chartData
  useEffect(() => {
    console.log('🟢 PowerProfileChart received:', {
      hasChartData: !!chartData,
      chartDataKeys: chartData ? Object.keys(chartData) : null,
      hasDatasets: !!chartData?.datasets,
      datasetLength: chartData?.datasets?.[0]?.data?.length,
      labelLength: chartData?.labels?.length,
      firstDataPoint: chartData?.datasets?.[0]?.data?.[0]
    });
  }, [chartData]);

  // Helper function to calculate moving average (backward-looking/causal)
  // Uses IECON2023 Paper Equation 17 approach
  const calculateMovingAverage = useCallback((data: number[], windowSize: number = 20): number[] => {
    const result: number[] = [];
    for (let i = 0; i < data.length; i++) {
      // Backward-looking window: only use current and previous values
      const start = Math.max(0, i - windowSize + 1);
      const end = i + 1;
      const window = data.slice(start, end);
      const avg = window.reduce((sum, val) => sum + val, 0) / window.length;
      result.push(avg);
    }
    return result;
  }, []);

  const drawChart = useCallback((maxDataPoints?: number) => {
    const canvas = canvasRef.current;
    if (!canvas || !chartData?.datasets?.[0]?.data) {
      console.log('🔴 drawChart skipped:', { canvas: !!canvas, hasData: !!chartData?.datasets?.[0]?.data });
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const fullData = chartData.datasets[0].data;
    const fullLabels = chartData.labels || fullData.map((_, i) => i);

    // 🔥 PERFORMANCE FIX: Limit data to max 5000 points before progressive rendering
    const MAX_POINTS = 5000;
    let processedFullData = fullData;
    let processedFullLabels = fullLabels;
    
    if (fullData.length > MAX_POINTS) {
      console.log(`⚡ PowerProfileChart: Downsampling from ${fullData.length} to ${MAX_POINTS} points`);
      const step = Math.ceil(fullData.length / MAX_POINTS);
      processedFullData = fullData.filter((_: number, index: number) => index % step === 0);
      processedFullLabels = fullLabels.filter((_: any, index: number) => index % step === 0);
      console.log(`✅ PowerProfileChart: Downsampled to ${processedFullData.length} points (step: ${step})`);
    }

    // Progressive rendering: only use first N datapoints
    const dataLimit = maxDataPoints || processedFullData.length;
    const originalData = processedFullData.slice(0, dataLimit);
    const labels = processedFullLabels.slice(0, dataLimit);

    console.log('🟢 Drawing chart with', originalData.length, '/', processedFullData.length, 'datapoints');

    // Calculate statistics
    const average = originalData.reduce((sum: number, val: number) => sum + val, 0) / originalData.length;
    
    // Dynamic window size: use larger window for smoother moving average
    // Typically 10-20% of data length for a smooth line like in the reference image
    const dynamicWindowSize = Math.max(20, Math.floor(originalData.length * 0.15));
    const movingAverage = calculateMovingAverage(originalData, dynamicWindowSize);
    
    console.log('🔵 Moving average window size:', dynamicWindowSize, 'out of', originalData.length, 'datapoints');

    // Chart dimensions
    const padding = 80;
    const paddingBottom = 100; // Extra space for legend at bottom
    const chartWidth = canvas.width - padding * 2;
    const chartHeight = canvas.height - padding - paddingBottom;

    // Data ranges
    const maxValue = Math.max(...originalData);
    const minValue = Math.min(0, Math.min(...originalData));
    const maxTime = Math.max(...labels.map(Number));

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = colors.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Helper functions
    const xScale = (time: number) => padding + (time / maxTime) * chartWidth;
    const yScale = (value: number) => padding + chartHeight - ((value - minValue) / (maxValue - minValue)) * chartHeight;

    // Draw grid
    ctx.strokeStyle = colors.grid;
    ctx.lineWidth = 1;

    // Horizontal grid lines
    for (let i = 0; i <= 10; i++) {
      const y = padding + (i / 10) * chartHeight;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(padding + chartWidth, y);
      ctx.stroke();
    }

    // Vertical grid lines
    for (let i = 0; i <= 10; i++) {
      const x = padding + (i / 10) * chartWidth;
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, padding + chartHeight);
      ctx.stroke();
    }

    // Draw axes
    ctx.strokeStyle = colors.text;
    ctx.lineWidth = 2;

    // Y-axis
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, padding + chartHeight);
    ctx.stroke();

    // X-axis
    ctx.beginPath();
    ctx.moveTo(padding, padding + chartHeight);
    ctx.lineTo(padding + chartWidth, padding + chartHeight);
    ctx.stroke();

    // Draw axis labels
    ctx.fillStyle = colors.text;
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';

    // X-axis label
    ctx.fillText('Time t [s]', canvas.width / 2, padding + chartHeight + 40);

    // Y-axis label (rotated)
    ctx.save();
    ctx.translate(20, canvas.height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Power P [W]', 0, 0);
    ctx.restore();

    // Draw Y-axis ticks and labels
    ctx.textAlign = 'right';
    ctx.font = '12px Arial';
    for (let i = 0; i <= 5; i++) {
      const value = minValue + (i / 5) * (maxValue - minValue);
      const y = yScale(value);

      // Tick mark
      ctx.beginPath();
      ctx.moveTo(padding - 5, y);
      ctx.lineTo(padding + 5, y);
      ctx.stroke();

      // Label
      ctx.fillText(Math.round(value).toString(), padding - 10, y + 5);
    }

    // Draw X-axis ticks and labels
    ctx.textAlign = 'center';
    for (let i = 0; i <= 5; i++) {
      const time = (i / 5) * maxTime;
      const x = xScale(time);

      // Tick mark
      ctx.beginPath();
      ctx.moveTo(x, padding + chartHeight - 5);
      ctx.lineTo(x, padding + chartHeight + 5);
      ctx.stroke();

      // Label
      ctx.fillText(Math.round(time).toString(), x, padding + chartHeight + 20);
    }

    // Draw original data (blue line for positive, green for negative/recuperation)
    ctx.lineWidth = 1;
    ctx.beginPath();
    
    for (let i = 0; i < originalData.length; i++) {
      const x = xScale(labels[i]);
      const y = yScale(originalData[i]);
      const value = originalData[i];
      
      // Change color based on positive/negative (recuperation)
      if (i === 0) {
        ctx.strokeStyle = value >= 0 ? colors.primary : (isDark ? '#66bb6a' : '#22c55e');
        ctx.beginPath();
        ctx.moveTo(x, y);
      } else {
        const prevValue = originalData[i - 1];
        // If sign changes, end current path and start new one with different color
        if ((value >= 0 && prevValue < 0) || (value < 0 && prevValue >= 0)) {
          ctx.stroke();
          ctx.strokeStyle = value >= 0 ? colors.primary : (isDark ? '#66bb6a' : '#22c55e');
          ctx.beginPath();
          ctx.moveTo(xScale(labels[i - 1]), yScale(prevValue));
          ctx.lineTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
    }
    ctx.stroke();

    // Draw moving average (color-coded: blue for positive, green for negative/recuperation)
    ctx.lineWidth = 2;
    ctx.beginPath();

    for (let i = 0; i < movingAverage.length; i++) {
      const x = xScale(labels[i]);
      const y = yScale(movingAverage[i]);
      const value = movingAverage[i];

      // Change color based on positive/negative (recuperation)
      if (i === 0) {
        ctx.strokeStyle = value >= 0 ? (isDark ? '#ffa726' : '#ff7f0e') : (isDark ? '#66bb6a' : '#22c55e');
        ctx.beginPath();
        ctx.moveTo(x, y);
      } else {
        const prevValue = movingAverage[i - 1];
        // If sign changes, end current path and start new one with different color
        if ((value >= 0 && prevValue < 0) || (value < 0 && prevValue >= 0)) {
          ctx.stroke();
          ctx.strokeStyle = value >= 0 ? (isDark ? '#ffa726' : '#ff7f0e') : (isDark ? '#66bb6a' : '#22c55e');
          ctx.beginPath();
          ctx.moveTo(xScale(labels[i - 1]), yScale(prevValue));
          ctx.lineTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
    }
    ctx.stroke();

    // Draw zero line (for recuperation visualization) - only if we have negative values
    if (minValue < 0) {
      ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      const zeroY = yScale(0);
      ctx.moveTo(padding, zeroY);
      ctx.lineTo(padding + chartWidth, zeroY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Label for zero line
      ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)';
      ctx.font = '11px Arial';
      ctx.textAlign = 'right';
      ctx.fillText('0 W (Baseline)', padding - 15, zeroY + 4);
    }

    // Draw average line (black dashed)
    ctx.strokeStyle = colors.text;
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(padding, yScale(average));
    ctx.lineTo(padding + chartWidth, yScale(average));
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw legend - horizontal unter der X-Achse
    const hasRecuperation = minValue < 0;
    const legendY = padding + chartHeight + 65;
    const legendItemWidth = 180;

    ctx.font = '12px Arial';
    ctx.textAlign = 'left';

    let legendItemX = padding;

    // Original Data (Power Consumption)
    ctx.strokeStyle = colors.primary;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(legendItemX, legendY);
    ctx.lineTo(legendItemX + 20, legendY);
    ctx.stroke();
    ctx.fillStyle = colors.text;
    ctx.fillText('Power Consumption', legendItemX + 25, legendY + 4);

    legendItemX += legendItemWidth;
    
    // Recuperation (if present)
    if (hasRecuperation) {
      ctx.strokeStyle = isDark ? '#66bb6a' : '#22c55e';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(legendItemX, legendY);
      ctx.lineTo(legendItemX + 20, legendY);
      ctx.stroke();
      ctx.fillStyle = colors.text;
      ctx.fillText('Recuperation', legendItemX + 25, legendY + 4);

      legendItemX += legendItemWidth;
    }

    // Moving Average
    ctx.strokeStyle = isDark ? '#ffa726' : '#ff7f0e';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(legendItemX, legendY);
    ctx.lineTo(legendItemX + 20, legendY);
    ctx.stroke();
    ctx.fillStyle = colors.text;
    ctx.fillText('Moving Average', legendItemX + 25, legendY + 4);

    legendItemX += legendItemWidth;

    // Average
    ctx.strokeStyle = colors.text;
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(legendItemX, legendY);
    ctx.lineTo(legendItemX + 20, legendY);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = colors.text;
    ctx.fillText('Average', legendItemX + 25, legendY + 4);
  }, [chartData, calculateMovingAverage, colors, isDark]);

  // Progressive rendering animation effect
  useEffect(() => {
    if (!chartData?.datasets?.[0]?.data) return;

    const totalPoints = chartData.datasets[0].data.length;

    // Only animate if we have more than 500 points
    if (totalPoints <= 500) {
      drawChart(totalPoints);
      return;
    }

    // Start animation
    setIsAnimating(true);
    setProgressiveIndex(0);

    // Animation parameters
    const startPoints = Math.min(100, totalPoints); // Start with 100 points
    const pointsPerFrame = Math.ceil((totalPoints - startPoints) / 60); // Complete in ~60 frames
    let currentPoints = startPoints;

    const animate = () => {
      if (currentPoints >= totalPoints) {
        setIsAnimating(false);
        drawChart(totalPoints);
        return;
      }

      currentPoints = Math.min(currentPoints + pointsPerFrame, totalPoints);
      setProgressiveIndex(currentPoints);
      drawChart(currentPoints);

      animationRef.current = requestAnimationFrame(animate);
    };

    // Start with initial points
    drawChart(startPoints);
    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [chartData, drawChart]);

  if (!chartData?.datasets?.[0]?.data) {
    return (
      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            {title}
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: height,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.100',
              borderRadius: 2,
            }}
          >
            <Typography variant='h6' color='text.secondary'>
              Chart wird geladen...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const totalDataPoints = chartData?.datasets?.[0]?.data?.length || 0;
  const loadingProgress = totalDataPoints > 0 ? (progressiveIndex / totalDataPoints) * 100 : 0;

  return (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Typography variant='h6' gutterBottom>
          {title}
        </Typography>
      )}

      {/* Progressive loading indicator */}
      {isAnimating && totalDataPoints > 500 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant='caption' color='text.secondary'>
              Lade Datenpunkte...
            </Typography>
            <Typography variant='caption' color='primary'>
              {progressiveIndex.toLocaleString()} / {totalDataPoints.toLocaleString()} Punkte ({loadingProgress.toFixed(0)}%)
            </Typography>
          </Box>
          <LinearProgress
            variant='determinate'
            value={loadingProgress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)'
              }
            }}
          />
        </Box>
      )}

      <Box sx={{ width: '100%', position: 'relative', borderRadius: '16px', overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          width={1200}
          height={height}
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            borderRadius: '16px'
          }}
        />
      </Box>
    </Box>
  );
};

export default PowerProfileChart;
